import { Spin } from 'antd';
import Button from 'antd/lib/button';
import { type FC, type ReactElement, useEffect, useState } from 'react';
import type { GetOrderHistoryQuery } from '@/generated/graphql/graphql';
import { useGetOrderHistory } from '../query/orders.hooks';
import { orderChangeFormatters } from './components/formatters';
import { OrderChangePreloaderView } from './components/OrderChangePreloaderView';
import { OrderChangeView } from './OrderChange/OrderChangeView';
import { OrderDriverInteractionView } from './OrderChange/OrderDriverInteractionView';
import { OrderUserViewsLogView } from './OrderChange/OrderUserViewsLogView';

export const OrderChangesView: FC<{ orderId: number }> = ({ orderId }) => {
  const [offset, setOffset] = useState(0);
  const {
    data: orderHistoryData,
    isLoading: isOrderHistoryLoading,
    isFetching: isOrderHistoryFetching,
    isError: isOrderHistoryError,
  } = useGetOrderHistory({
    orderId,
    offset,
  });

  const [changes, setChanges] = useState<GetOrderHistoryQuery['order_change']>([]);

  const total = orderHistoryData?.order_change_aggregate?.aggregate?.count || 0;

  useEffect(() => {
    if (orderHistoryData) {
      setChanges((prevChanges) => {
        const newChanges = orderHistoryData.order_change;
        const existingIds = new Set(prevChanges.map((c) => c.id));
        const uniqueNewChanges = newChanges.filter((c) => !existingIds.has(c.id));

        if (uniqueNewChanges.length === 0) {
          return prevChanges;
        }

        return [...prevChanges, ...uniqueNewChanges];
      });
    }
  }, [orderHistoryData]);

  const fetchMore = () => {
    setOffset((prevOffset) => prevOffset + 20);
  };

  const wrapper = (child: ReactElement | string) => (
    <Spin spinning={isOrderHistoryFetching}>
      <h2 style={{ textAlign: 'center' }}>History</h2>
      <div
        style={{
          minHeight: 430,
          maxHeight: '70vh',
          position: 'relative',
          fontSize: 16,
          overflowY: 'auto',
        }}
      >
        {child}
      </div>
    </Spin>
  );

  if (isOrderHistoryLoading && offset === 0) {
    return wrapper(
      <div>
        {[1, 2].map((id) => (
          <OrderChangePreloaderView key={`preloader-${id}`} />
        ))}
      </div>
    );
  }

  if (isOrderHistoryError) {
    return wrapper(<div>Error</div>);
  }

  if (total === 0 && offset === 0) {
    return wrapper(
      <div
        style={{
          display: 'flex',
          position: 'absolute',
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        No changes yet.
      </div>
    );
  }

  const filteredChangeList = changes.filter(
    (change) =>
      // @ts-expect-error
      Object.keys(change.old).findIndex((v) => orderChangeFormatters.includes(v)) !== -1
  );

  const sortedChangesAndUserViewsLogs = [
    ...filteredChangeList,
    ...(orderHistoryData?.user_views_log || []),
    ...(orderHistoryData?.driver_order_interaction || []),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return wrapper(
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      {sortedChangesAndUserViewsLogs.map((changeOrUserViewLog) => {
        if ('editor' in changeOrUserViewLog) {
          const change = changeOrUserViewLog as GetOrderHistoryQuery['order_change'][0];
          return <OrderChangeView key={`change-${change.id}`} change={change} />;
        }

        if ('contact_type' in changeOrUserViewLog) {
          const interaction =
            changeOrUserViewLog as GetOrderHistoryQuery['driver_order_interaction'][0];
          return (
            <OrderDriverInteractionView
              key={`interaction-${interaction.id}`}
              driverOrderInteraction={interaction}
            />
          );
        }

        const log = changeOrUserViewLog as GetOrderHistoryQuery['user_views_log'][0];
        return <OrderUserViewsLogView key={`log-${log.id}`} userViewsLog={log} />;
      })}

      {total > changes.length && (
        <Button
          loading={isOrderHistoryFetching}
          type="link"
          onClick={fetchMore}
          style={{ marginTop: 5 }}
        >
          Load more
          <p>↓</p>
        </Button>
      )}
    </div>
  );
};
